import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { i18next } from '@yola/ws-sdk';
import getUniqueValues from 'src/js/modules/utils/get-unique-values';
import elementSettings from 'src/js/modules/element-settings';
import useAutoPaddingSync from '../hooks/use-auto-padding-sync';
import controlIds from '../constants/control-ids';
import gapDefaults from '../constants/gap-defaults';
import paddingDefaults from '../constants/padding-defaults';
import buildSpacingBreakpointBatch, {
  SPACING_OFF_VALUE,
} from '../helpers/build-spacing-breakpoint-batch';

const {
  Context: ElementSettingsContext,
  components: { ToggleableSection },
  helpers: { combineBatches, getBatchValues },
} = elementSettings;

const getSpacingDefaultsByBreakpoint = (breakpointKey) => ({
  [breakpointKey]: {
    ...paddingDefaults[breakpointKey],
    ...gapDefaults,
  },
});

function SpacingSectionControl({ elementIds, targets, onChange, children }) {
  const { state, resolvedState, deviceMode } = useContext(ElementSettingsContext);
  const { getAutoPaddingBatch } = useAutoPaddingSync();

  const allElementIds = useMemo(
    () => getUniqueValues(Object.values(elementIds).flat()),
    [elementIds]
  );
  const paddingElementIds = useMemo(
    () =>
      getUniqueValues(
        [controlIds.VERTICAL_SPACING, controlIds.HORIZONTAL_SPACING].flatMap(
          (controlId) => elementIds[controlId] || []
        )
      ),
    [elementIds]
  );
  const allTargets = useMemo(() => getUniqueValues(Object.values(targets).flat()), [targets]);

  const isToggleOn = useMemo(() => {
    const values = getBatchValues(resolvedState, {
      elementIds: allElementIds,
      targets: allTargets,
    }).flat();

    return values.some((value) => value && value !== SPACING_OFF_VALUE);
  }, [resolvedState, allElementIds, allTargets]);

  const handleToggleChange = useCallback(
    (isActive) => {
      const breakpointKey = deviceMode?.breakpointKey;
      const defaultsByBreakpoint = getSpacingDefaultsByBreakpoint(breakpointKey);
      const breakpointBasedBatch = buildSpacingBreakpointBatch({
        elementIds,
        targets,
        defaultsByBreakpoint,
        breakpointKey,
        isEnabled: isActive,
        state,
      });

      onChange({
        batch: combineBatches([
          breakpointBasedBatch,
          getAutoPaddingBatch(breakpointBasedBatch, { elementIds: paddingElementIds }),
        ]),
        controlId: controlIds.SPACING_SECTION,
        value: isActive,
      });
    },
    [paddingElementIds, deviceMode, elementIds, getAutoPaddingBatch, onChange, state, targets]
  );

  return (
    <ToggleableSection
      value={isToggleOn}
      title={i18next.t('Spacing')}
      onChange={handleToggleChange}
    >
      {children}
    </ToggleableSection>
  );
}

SpacingSectionControl.propTypes = {
  elementIds: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  targets: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

export default SpacingSectionControl;
